(function () {
  const themeSettings = window.themeSettings || {};
  const themeStrings = window.themeStrings || {};
  const routes = window.routes || {};
  const prefersReduced = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  const motionLevel = prefersReduced ? 'none' : (themeSettings.animationIntensity || 'full');
  const reducedMotion = motionLevel === 'none';

  const selectors = {
    backdrop: document.querySelector('[data-drawer-backdrop]'),
    header: document.querySelector('[data-site-header]'),
    cartDrawer: document.querySelector('[data-cart-drawer]'),
    cartItems: document.querySelector('[data-cart-items]'),
    cartFooter: document.querySelector('[data-cart-footer]'),
    cartEmpty: document.querySelector('[data-cart-empty]'),
    cartSubtotal: document.querySelector('[data-cart-subtotal]'),
    cartToggles: document.querySelectorAll('[data-cart-toggle]'),
    searchPanel: document.querySelector('[data-search-panel]'),
    searchForm: document.querySelector('[data-search-form]'),
    searchInput: document.querySelector('.search-panel__input'),
    searchResults: document.querySelector('[data-predictive-search-results]'),
    searchViewAll: document.querySelector('[data-search-view-all]'),
    searchToggles: document.querySelectorAll('[data-search-toggle]'),
    themeToggle: document.querySelector('[data-theme-toggle]'),
    themeToggleLabel: document.querySelector('[data-theme-toggle-label]'),
    mobileDrawer: document.querySelector('[data-mobile-drawer]'),
    mobileToggle: document.querySelector('[data-mobile-nav-toggle]'),
    mobileClose: document.querySelector('[data-mobile-nav-close]')
  };

  let activePanel = null;
  let lastScrollY = window.scrollY;
  let headerTicking = false;
  let colorScheme = document.documentElement.getAttribute('data-color-scheme') || 'light';
  let lastPanelTrigger = null;
  let transitionFallbackTimer = null;
  const productGalleries = new Map();

  function formatMoney(cents) {
    const currency = themeSettings.currencyCode || 'USD';
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency
    }).format((Number(cents) || 0) / 100);
  }

  function createRipple(event) {
    const target = event.currentTarget;
    if (!target || reducedMotion) return;

    const rect = target.getBoundingClientRect();
    const ripple = document.createElement('span');
    const size = Math.max(rect.width, rect.height);

    ripple.className = 'ripple-spot';
    ripple.style.width = `${size}px`;
    ripple.style.height = `${size}px`;
    ripple.style.left = `${event.clientX - rect.left}px`;
    ripple.style.top = `${event.clientY - rect.top}px`;

    target.appendChild(ripple);
    ripple.addEventListener('animationend', () => ripple.remove(), { once: true });
  }

  function applyColorScheme(nextScheme, persist) {
    colorScheme = nextScheme === 'dark' ? 'dark' : 'light';
    document.documentElement.setAttribute('data-color-scheme', colorScheme);

    const label = selectors.themeToggleLabel;
    const nextLabel = colorScheme === 'dark'
      ? (themeStrings.themeLight || 'Light')
      : (themeStrings.themeDark || 'Dark');

    if (label) label.textContent = nextLabel;
    if (selectors.themeToggle) {
      selectors.themeToggle.setAttribute('aria-label', `${nextLabel} mode`);
      selectors.themeToggle.setAttribute('data-theme-state', colorScheme);
    }

    const metaTheme = document.querySelector('meta[name="theme-color"]');
    if (metaTheme) {
      metaTheme.setAttribute('content', colorScheme === 'dark' ? '#120c0a' : '#E8E0D3');
    }

    if (persist) {
      try {
        localStorage.setItem('komasha-color-scheme', colorScheme);
      } catch (error) {}
    }
  }

  function escapeHtml(value) {
    const map = {
      '&': '&amp;',
      '<': '&lt;',
      '>': '&gt;',
      '"': '&quot;',
      "'": '&#39;'
    };

    return String(value || '').replace(/[&<>"']/g, (character) => map[character]);
  }

  function showBackdrop() {
    if (!selectors.backdrop) return;
    selectors.backdrop.hidden = false;
    requestAnimationFrame(() => selectors.backdrop.classList.add('is-visible'));
    document.body.classList.add('is-locked');
  }

  function hideBackdrop() {
    if (!selectors.backdrop) return;
    selectors.backdrop.classList.remove('is-visible');
    window.setTimeout(() => {
      if (activePanel) return;
      selectors.backdrop.hidden = true;
      document.body.classList.remove('is-locked');
    }, 220);
  }

  function openPanel(name) {
    if (activePanel) closePanel(activePanel, false);
    lastPanelTrigger = document.activeElement;
    activePanel = name;

    if (name === 'cart' && selectors.cartDrawer) {
      selectors.cartDrawer.hidden = false;
      selectors.cartDrawer.classList.add('is-open');
      selectors.cartDrawer.setAttribute('aria-hidden', 'false');
    }

    if (name === 'search' && selectors.searchPanel) {
      selectors.searchPanel.hidden = false;
      requestAnimationFrame(() => selectors.searchPanel.classList.add('is-open'));
      selectors.searchInput?.focus();
    }

    if (name === 'mobile' && selectors.mobileDrawer) {
      selectors.header?.classList.add('has-open-drawer');
      selectors.mobileDrawer.hidden = false;
      selectors.mobileDrawer.setAttribute('aria-hidden', 'false');
      selectors.mobileToggle?.classList.add('is-open');
      selectors.mobileToggle?.setAttribute('aria-expanded', 'true');
      selectors.mobileToggle?.setAttribute('aria-label', 'Close menu');
      requestAnimationFrame(() => {
        selectors.mobileDrawer.classList.add('is-open');
        selectors.mobileClose?.focus();
      });
    }

    showBackdrop();
  }

  function closePanel(name, restoreFocus = true) {
    if (!name) return;

    const triggerToRestore = lastPanelTrigger;

    if (name === 'cart' && selectors.cartDrawer) {
      selectors.cartDrawer.classList.remove('is-open');
      selectors.cartDrawer.setAttribute('aria-hidden', 'true');
    }

    if (name === 'search' && selectors.searchPanel) {
      selectors.searchPanel.classList.remove('is-open');
    }

    if (name === 'mobile' && selectors.mobileDrawer) {
      selectors.header?.classList.remove('has-open-drawer');
      selectors.mobileDrawer.classList.remove('is-open');
      selectors.mobileDrawer.setAttribute('aria-hidden', 'true');
      selectors.mobileToggle?.classList.remove('is-open');
      selectors.mobileToggle?.setAttribute('aria-expanded', 'false');
      selectors.mobileToggle?.setAttribute('aria-label', 'Open menu');
    }

    window.setTimeout(() => {
      if (activePanel === name) return;
      if (name === 'cart' && selectors.cartDrawer) selectors.cartDrawer.hidden = true;
      if (name === 'search' && selectors.searchPanel) selectors.searchPanel.hidden = true;
      if (name === 'mobile' && selectors.mobileDrawer) selectors.mobileDrawer.hidden = true;
      if (restoreFocus && triggerToRestore?.isConnected) triggerToRestore.focus();
    }, 260);

    activePanel = null;
    hideBackdrop();
  }

  function animateNumber(element, nextValue) {
    if (!element) return;

    const previous = Number(element.dataset.cartSubtotalCents || 0);
    const target = Number(nextValue || 0);
    const duration = reducedMotion ? 0 : 450;

    if (!duration || previous === target) {
      element.textContent = formatMoney(target);
      element.dataset.cartSubtotalCents = `${target}`;
      return;
    }

    const start = performance.now();

    function tick(now) {
      const progress = Math.min((now - start) / duration, 1);
      const eased = 1 - Math.pow(1 - progress, 3);
      const current = Math.round(previous + ((target - previous) * eased));
      element.textContent = formatMoney(current);

      if (progress < 1) {
        requestAnimationFrame(tick);
      } else {
        element.dataset.cartSubtotalCents = `${target}`;
        element.classList.remove('is-animating');
      }
    }

    element.classList.add('is-animating');
    requestAnimationFrame(tick);
  }

  function updateCartBadge(count) {
    document.querySelectorAll('[data-cart-count]').forEach((badge) => {
      badge.textContent = count;
      badge.classList.remove('is-updated');
      requestAnimationFrame(() => badge.classList.add('is-updated'));
    });

    document.querySelectorAll('.cart-icon').forEach((icon) => {
      icon.classList.remove('is-bouncing');
      requestAnimationFrame(() => icon.classList.add('is-bouncing'));
    });
  }

  function cartItemMarkup(item, index) {
    const imageMarkup = item.featured_image
      ? `<img src="${item.featured_image.url}" alt="${item.featured_image.alt || item.product_title}">`
      : '';

    return `
      <article class="cart-drawer__item" data-cart-line="${index + 1}">
        <a href="${item.url}" class="cart-drawer__item-image">${imageMarkup}</a>
        <div class="cart-drawer__item-meta">
          <h3 class="font-display"><a href="${item.url}">${item.product_title}</a></h3>
          ${item.variant_title && item.variant_title !== 'Default Title' ? `<p class="cart-drawer__variant">${item.variant_title}</p>` : ''}
          <div class="cart-drawer__meta-row">
            <span class="cart-drawer__line-total font-display">${formatMoney(item.final_line_price)}</span>
            <button type="button" class="cart-drawer__remove" data-cart-remove="${index + 1}">Remove</button>
          </div>
          <div class="quantity-stepper" data-qty-stepper data-cart-line-stepper="${index + 1}">
            <div class="quantity-stepper__controls">
              <button type="button" class="qty-btn" data-cart-qty="${index + 1}" data-qty-delta="-1" aria-label="Decrease quantity">−</button>
              <span class="qty-display">${item.quantity}</span>
              <button type="button" class="qty-btn" data-cart-qty="${index + 1}" data-qty-delta="1" aria-label="Increase quantity">+</button>
            </div>
          </div>
        </div>
      </article>
    `;
  }

  function renderCart(cart) {
    if (!selectors.cartItems || !selectors.cartEmpty || !selectors.cartFooter || !selectors.cartSubtotal) return;

    selectors.cartItems.innerHTML = cart.items.map(cartItemMarkup).join('');
    selectors.cartEmpty.hidden = cart.item_count > 0;
    selectors.cartFooter.hidden = cart.item_count === 0;
    animateNumber(selectors.cartSubtotal, cart.total_price);
    updateCartBadge(cart.item_count);
  }

  async function fetchCart() {
    const response = await fetch(`${routes.cart_url}.js`, {
      headers: { Accept: 'application/json' }
    });

    if (!response.ok) {
      throw new Error('Cart request failed');
    }

    return response.json();
  }

  async function syncCart() {
    try {
      const cart = await fetchCart();
      renderCart(cart);
      return cart;
    } catch (error) {
      console.error(error);
      return null;
    }
  }

  async function changeCartLine(line, quantity) {
    const response = await fetch(`${routes.cart_change_url}.js`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        Accept: 'application/json'
      },
      body: JSON.stringify({ line, quantity })
    });

    if (!response.ok) {
      throw new Error('Cart update failed');
    }

    return response.json();
  }

  function setButtonState(button, state, fallbackLabel) {
    if (!button) return;

    const label = button.querySelector('[data-button-label]');
    button.classList.remove('is-loading', 'is-success');

    if (state === 'loading') {
      button.classList.add('is-loading');
    }

    if (state === 'success') {
      button.classList.add('is-success');
    }

    if (label && fallbackLabel) {
      label.textContent = fallbackLabel;
    }
  }

  function updateHeaderState() {
    if (!selectors.header) return;

    const currentScroll = window.scrollY;
    const movingDown = currentScroll > lastScrollY;

    selectors.header.classList.toggle('is-hidden', movingDown && currentScroll > 180 && !activePanel);
    selectors.header.classList.toggle('is-elevated', currentScroll > 18);

    lastScrollY = currentScroll;
    headerTicking = false;
  }

  function bindHeaderScroll() {
    window.addEventListener('scroll', () => {
      if (headerTicking) return;
      headerTicking = true;
      requestAnimationFrame(updateHeaderState);
    }, { passive: true });
  }

  function bindPanelControls() {
    selectors.searchToggles.forEach((toggle) => {
      toggle.addEventListener('click', () => openPanel('search'));
    });

    selectors.cartToggles.forEach((toggle) => {
      toggle.addEventListener('click', async () => {
        await syncCart();
        openPanel('cart');
      });
    });

    selectors.mobileToggle?.addEventListener('click', () => {
      if (activePanel === 'mobile') {
        closePanel('mobile');
      } else {
        openPanel('mobile');
      }
    });

    document.querySelector('[data-search-close]')?.addEventListener('click', () => closePanel('search'));
    document.querySelector('[data-cart-close]')?.addEventListener('click', () => closePanel('cart'));
    selectors.mobileClose?.addEventListener('click', () => closePanel('mobile'));
    selectors.backdrop?.addEventListener('click', () => closePanel(activePanel));

    document.addEventListener('keydown', (event) => {
      if (event.key === 'Escape') {
        closePanel(activePanel);
        return;
      }

      if (event.key !== 'Tab' || activePanel !== 'mobile' || !selectors.mobileDrawer) return;

      const focusable = Array.from(selectors.mobileDrawer.querySelectorAll(
        'a[href], button:not([disabled]), input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])'
      )).filter((element) => element.offsetParent !== null);

      if (!focusable.length) return;

      const first = focusable[0];
      const last = focusable[focusable.length - 1];
      if (event.shiftKey && document.activeElement === first) {
        event.preventDefault();
        last.focus();
      } else if (!event.shiftKey && document.activeElement === last) {
        event.preventDefault();
        first.focus();
      }
    });

    if (selectors.mobileDrawer) {
      let touchStartX = 0;
      selectors.mobileDrawer.addEventListener('touchstart', (event) => {
        touchStartX = event.changedTouches[0].screenX;
      }, { passive: true });

      selectors.mobileDrawer.addEventListener('touchend', (event) => {
        const delta = event.changedTouches[0].screenX - touchStartX;
        const isRtl = document.documentElement.dir === 'rtl';
        if ((!isRtl && delta > 50) || (isRtl && delta < -50)) closePanel('mobile');
      }, { passive: true });
    }
  }

  function bindThemeToggle() {
    applyColorScheme(colorScheme, false);

    selectors.themeToggle?.addEventListener('click', () => {
      applyColorScheme(colorScheme === 'dark' ? 'light' : 'dark', true);
    });
  }

  function renderPredictiveResults(query, products) {
    if (!selectors.searchResults || !selectors.searchViewAll) return;

    if (!query) {
      selectors.searchResults.innerHTML = `
        <div class="search-panel__empty-state">
          <p>Start typing to search the collection.</p>
        </div>
      `;
      selectors.searchViewAll.hidden = true;
      selectors.searchViewAll.href = routes.search_url;
      return;
    }

    selectors.searchViewAll.hidden = false;
    selectors.searchViewAll.href = `${routes.search_url}?type=product&q=${encodeURIComponent(query)}`;

    if (!products.length) {
      selectors.searchResults.innerHTML = `
        <div class="search-panel__empty-state">
          <p>${escapeHtml(themeStrings.searchNoResults || 'No pieces matched that search')}</p>
        </div>
      `;
      return;
    }

    selectors.searchResults.innerHTML = products.map((product) => {
      const imageMarkup = product.image
        ? `<img src="${product.image}" alt="${escapeHtml(product.title)}">`
        : '<div class="search-panel__empty-state"><p>K</p></div>';

      return `
        <a href="${product.url}" class="search-result-card">
          ${imageMarkup}
          <div>
            <h3 class="font-display">${escapeHtml(product.title)}</h3>
            <p>${escapeHtml(product.vendor || 'KOMASHA')}</p>
            <strong class="font-display">${escapeHtml(product.price || '')}</strong>
          </div>
        </a>
      `;
    }).join('');
  }

  function bindPredictiveSearch() {
    if (!selectors.searchInput || !selectors.searchResults) return;

    let controller = null;
    let timer = null;

    async function fetchPredictiveResults(query) {
      if (controller) controller.abort();
      controller = new AbortController();

      try {
        const url = `${routes.predictive_search_url}?q=${encodeURIComponent(query)}&resources[type]=product&resources[limit]=4&section_id=predictive-search`;
        const response = await fetch(url, {
          headers: { Accept: 'text/html' },
          signal: controller.signal
        });

        if (!response.ok) throw new Error('Predictive search failed');

        const markup = await response.text();
        const doc = new DOMParser().parseFromString(markup, 'text/html');
        const fragment = doc.querySelector('[data-predictive-search-section]');

        selectors.searchResults.innerHTML = fragment
          ? fragment.innerHTML
          : `
              <div class="search-panel__empty-state">
                <p>${escapeHtml(themeStrings.searchNoResults || 'No pieces matched that search')}</p>
              </div>
            `;

        if (selectors.searchViewAll) {
          selectors.searchViewAll.hidden = false;
          selectors.searchViewAll.href = `${routes.search_url}?type=product&q=${encodeURIComponent(query)}`;
        }
      } catch (error) {
        if (error.name === 'AbortError') return;
        console.error(error);
        renderPredictiveResults(query, []);
      }
    }

    selectors.searchInput.addEventListener('input', (event) => {
      const query = event.target.value.trim();
      window.clearTimeout(timer);

      if (!query) {
        renderPredictiveResults('', []);
        return;
      }

      timer = window.setTimeout(() => fetchPredictiveResults(query), 180);
    });

    selectors.searchForm?.addEventListener('reset', () => renderPredictiveResults('', []));
  }

  function resetTransientPageState() {
    window.clearTimeout(transitionFallbackTimer);
    transitionFallbackTimer = null;

    const transitionOverlay = document.querySelector('.page-transition-overlay');
    transitionOverlay?.classList.add('is-resetting');

    document.body.classList.remove('is-transitioning', 'is-locked');
    selectors.backdrop?.classList.remove('is-visible');
    if (selectors.backdrop) selectors.backdrop.hidden = true;

    selectors.mobileDrawer?.classList.remove('is-open');
    selectors.header?.classList.remove('has-open-drawer');
    if (selectors.mobileDrawer) {
      selectors.mobileDrawer.hidden = true;
      selectors.mobileDrawer.setAttribute('aria-hidden', 'true');
    }
    selectors.mobileToggle?.classList.remove('is-open');
    selectors.mobileToggle?.setAttribute('aria-expanded', 'false');
    selectors.mobileToggle?.setAttribute('aria-label', 'Open menu');

    selectors.cartDrawer?.classList.remove('is-open');
    if (selectors.cartDrawer) {
      selectors.cartDrawer.hidden = true;
      selectors.cartDrawer.setAttribute('aria-hidden', 'true');
    }

    selectors.searchPanel?.classList.remove('is-open');
    if (selectors.searchPanel) selectors.searchPanel.hidden = true;

    activePanel = null;

    if (transitionOverlay) {
      void transitionOverlay.offsetWidth;
      requestAnimationFrame(() => transitionOverlay.classList.remove('is-resetting'));
    }
  }

  function bindPageTransitions() {
    window.addEventListener('pageshow', resetTransientPageState);
    resetTransientPageState();

    document.addEventListener('click', (event) => {
      const link = event.target.closest('a[href]');
      if (!link) return;

      const url = new URL(link.href, window.location.origin);
      const isSameOrigin = url.origin === window.location.origin;
      const isHashOnly = url.pathname === window.location.pathname && url.hash;
      const isFile = link.hasAttribute('download');
      const isNewTab = link.target === '_blank' || event.metaKey || event.ctrlKey || event.shiftKey || event.altKey;
      const isSpecial = link.dataset.noTransition !== undefined || link.href.startsWith('mailto:') || link.href.startsWith('tel:');

      if (!isSameOrigin || isHashOnly || isFile || isNewTab || isSpecial) return;

      event.preventDefault();
      document.body.classList.add('is-transitioning');
      window.setTimeout(() => {
        window.location.assign(url.toString());
      }, reducedMotion ? 0 : 520);
      transitionFallbackTimer = window.setTimeout(resetTransientPageState, 8000);
    });
  }

  function bindButtonRipples() {
    document.querySelectorAll('.btn-primary').forEach((button) => {
      button.addEventListener('click', createRipple);
    });
  }

  function bindAccordion() {
    document.querySelectorAll('[data-accordion-trigger]').forEach((trigger) => {
      trigger.addEventListener('click', () => {
        const body = trigger.nextElementSibling;
        const isOpen = trigger.classList.contains('is-open');

        document.querySelectorAll('[data-accordion-trigger]').forEach((button) => {
          if (button !== trigger) {
            button.classList.remove('is-open');
            const nextBody = button.nextElementSibling;
            if (nextBody) nextBody.style.maxHeight = '0px';
          }
        });

        trigger.classList.toggle('is-open', !isOpen);
        if (!body) return;
        body.style.maxHeight = !isOpen ? `${body.scrollHeight}px` : '0px';
      });
    });
  }

  function setQtyValue(stepper, nextValue) {
    const input = stepper.querySelector('[data-qty-input]');
    const display = stepper.querySelector('[data-qty-display], .qty-display');
    const bounded = Math.max(0, Number(nextValue) || 0);

    if (input) input.value = bounded;
    if (display) {
      display.classList.add('is-flipping');
      window.setTimeout(() => {
        display.textContent = bounded;
        display.classList.remove('is-flipping');
      }, 120);
    }
  }

  function bindQuantitySteppers() {
    document.querySelectorAll('[data-qty-stepper]').forEach((stepper) => {
      stepper.addEventListener('click', (event) => {
        const button = event.target.closest('[data-qty-action]');
        if (!button) return;

        const input = stepper.querySelector('[data-qty-input]');
        const current = Number(input?.value || 1);
        const delta = button.dataset.qtyAction === 'increase' ? 1 : -1;
        const minimum = input && input.min ? Number(input.min) : 1;
        const nextValue = Math.max(minimum, current + delta);
        setQtyValue(stepper, nextValue);
      });
    });
  }

  function bindProductForms() {
    document.querySelectorAll('[data-product-form]').forEach((form) => {
      const variantsNode = form.querySelector('[data-product-variants]');
      const currentVariantNode = form.querySelector('[data-current-variant]');
      const variantInput = form.querySelector('[data-variant-id]');
      const addButton = form.querySelector('[data-add-to-cart]');
      const priceElement = document.querySelector('[data-product-price]');
      const compareElement = document.querySelector('[data-product-compare-price]');
      const variants = variantsNode ? JSON.parse(variantsNode.textContent) : [];
      const initialVariant = currentVariantNode ? JSON.parse(currentVariantNode.textContent) : null;

      function findVariant() {
        if (!variants.length) return null;

        const selectedValues = Array.from(form.querySelectorAll('.variant-pills input:checked'))
          .map((input) => input.value);

        return variants.find((variant) =>
          variant.options.every((optionValue, index) => optionValue === selectedValues[index])
        ) || null;
      }

      function firstAvailableVariant() {
        return variants.find((variant) => variant.available) || variants[0] || null;
      }

      function setVariantSelection(variant) {
        if (!variant) return;

        form.querySelectorAll('.variant-pills').forEach((group, index) => {
          const desiredValue = variant.options[index];
          if (!desiredValue) return;

          const optionInputs = Array.from(group.querySelectorAll('input'));
          optionInputs.forEach((input) => {
            input.checked = input.value === desiredValue;
          });
        });
      }

      function submitFormFallback() {
        HTMLFormElement.prototype.submit.call(form);
      }

      function syncVariant() {
        let variant = findVariant();

        if (!variant || !variant.available) {
          variant = firstAvailableVariant();
          setVariantSelection(variant);
        }

        if (!variant) {
          addButton?.setAttribute('disabled', 'disabled');
          if (priceElement) priceElement.textContent = themeStrings.soldOut || 'Unavailable';
          return;
        }

        if (variantInput) variantInput.value = variant.id;
        if (priceElement) {
          priceElement.textContent = formatMoney(variant.price);
          priceElement.classList.toggle('product-page__price--sale', variant.compare_at_price > variant.price);
        }

        if (compareElement) {
          if (variant.compare_at_price > variant.price) {
            compareElement.textContent = formatMoney(variant.compare_at_price);
            compareElement.hidden = false;
          } else {
            compareElement.hidden = true;
          }
        }

        if (addButton) {
          addButton.disabled = !variant.available;
          const label = addButton.querySelector('[data-button-label]');
          if (label) {
            label.textContent = variant.available ? (themeStrings.addToCart || 'Add to cart') : (themeStrings.soldOut || 'Sold out');
          }
        }

        const galleryRoot = document.querySelector('[data-product-gallery-root]');
        const gallery = galleryRoot ? productGalleries.get(galleryRoot) : null;
        if (gallery) {
          const featuredId = variant.featured_media?.id || variant.featured_image?.id;
          gallery.activateVariant(variant.id, featuredId);
        }
      }

      form.querySelectorAll('.variant-pills input').forEach((input) => {
        input.addEventListener('change', syncVariant);
      });

      form.addEventListener('submit', async (event) => {
        event.preventDefault();
        if (!addButton) {
          submitFormFallback();
          return;
        }

        if (addButton.disabled) return;

        const labelNode = addButton.querySelector('[data-button-label]');
        const idleLabel = labelNode?.textContent || themeStrings.addToCart || 'Add to cart';
        setButtonState(addButton, 'loading', idleLabel);

        try {
          const response = await fetch(`${routes.cart_add_url}.js`, {
            method: 'POST',
            headers: {
              Accept: 'application/json'
            },
            body: new FormData(form)
          });

          if (!response.ok) throw new Error(`Add to cart failed: ${response.status}`);

          await response.json();
          const cart = await syncCart();
          setButtonState(addButton, 'success', themeStrings.addedToCart || 'Added ✓');

          window.setTimeout(() => {
            setButtonState(addButton, '', idleLabel);
          }, 1800);

          if (cart) openPanel('cart');
        } catch (error) {
          console.error(error);
          submitFormFallback();
        }
      });

      if (initialVariant && !initialVariant.available) {
        setVariantSelection(firstAvailableVariant());
      }

      syncVariant();
    });
  }

  function bindProductGallery() {
    document.querySelectorAll('[data-product-gallery-root]').forEach((gallery) => {
      const mediaItems = Array.from(gallery.querySelectorAll('[data-media-id]'));
      const thumbs = Array.from(gallery.querySelectorAll('[data-thumb-target]'));
      let currentIndex = 0;
      let touchStartX = 0;
      let carouselTimer = null;

      // Build variant→imageIds map from the product images JSON (includes variant_ids)
      const variantImageMap = {};
      const imagesNode = gallery.closest('[data-product-form]')
        ? gallery.closest('[data-product-form]').querySelector('[data-product-images]')
        : document.querySelector('[data-product-images]');

      if (imagesNode) {
        try {
          const images = JSON.parse(imagesNode.textContent);
          images.forEach((img) => {
            if (Array.isArray(img.variant_ids) && img.variant_ids.length) {
              img.variant_ids.forEach((vid) => {
                if (!variantImageMap[vid]) variantImageMap[vid] = [];
                variantImageMap[vid].push(String(img.id));
              });
            }
          });
        } catch (e) {}
      }

      function activateMediaByIndex(index) {
        currentIndex = (index + mediaItems.length) % mediaItems.length;
        const activeMedia = mediaItems[currentIndex];

        mediaItems.forEach((item) => item.classList.toggle('is-active', item === activeMedia));
        thumbs.forEach((thumb) => {
          thumb.classList.toggle('is-active', thumb.dataset.thumbTarget === activeMedia.dataset.mediaId);
        });
      }

      function stopCarousel() {
        if (carouselTimer) {
          clearInterval(carouselTimer);
          carouselTimer = null;
        }
      }

      function startCarousel(eligibleItems) {
        stopCarousel();
        if (eligibleItems.length <= 1) return;
        let step = 0;
        carouselTimer = setInterval(() => {
          step = (step + 1) % eligibleItems.length;
          const idx = mediaItems.indexOf(eligibleItems[step]);
          if (idx >= 0) activateMediaByIndex(idx);
        }, 2500);
      }

      function activateVariant(variantId, featuredImageId) {
        stopCarousel();

        // Try variant-specific images from the images JSON map
        const imageIds = variantImageMap[variantId];
        if (imageIds && imageIds.length) {
          const eligibleItems = mediaItems.filter((item) => imageIds.includes(item.dataset.mediaId));
          if (eligibleItems.length) {
            const firstIdx = mediaItems.indexOf(eligibleItems[0]);
            if (firstIdx >= 0) activateMediaByIndex(firstIdx);
            startCarousel(eligibleItems);
            return;
          }
        }

        // Fallback: use data-variant-ids attribute on figures (set by Liquid)
        const taggedItems = mediaItems.filter((item) => {
          const vids = item.dataset.variantIds;
          return vids && vids.split(',').map((s) => s.trim()).includes(String(variantId));
        });
        if (taggedItems.length) {
          const firstIdx = mediaItems.indexOf(taggedItems[0]);
          if (firstIdx >= 0) activateMediaByIndex(firstIdx);
          startCarousel(taggedItems);
          return;
        }

        // Final fallback: jump to featured image only
        if (featuredImageId) {
          const idx = mediaItems.findIndex((item) => item.dataset.mediaId === String(featuredImageId));
          if (idx >= 0) activateMediaByIndex(idx);
        }
      }

      thumbs.forEach((thumb) => {
        thumb.addEventListener('click', () => {
          stopCarousel();
          const targetIndex = mediaItems.findIndex((item) => item.dataset.mediaId === thumb.dataset.thumbTarget);
          if (targetIndex >= 0) activateMediaByIndex(targetIndex);
        });
      });

      const main = gallery.querySelector('.product-gallery__main');
      main?.addEventListener('touchstart', (event) => {
        touchStartX = event.changedTouches[0].clientX;
      }, { passive: true });

      main?.addEventListener('touchend', (event) => {
        const delta = event.changedTouches[0].clientX - touchStartX;
        if (Math.abs(delta) < 40) return;
        stopCarousel();
        activateMediaByIndex(currentIndex + (delta < 0 ? 1 : -1));
      }, { passive: true });

      productGalleries.set(gallery, { activateVariant, stopCarousel, mediaItems });
    });
  }

  function bindCartDrawerActions() {
    selectors.cartDrawer?.addEventListener('click', async (event) => {
      const qtyButton = event.target.closest('[data-cart-qty]');
      const removeLink = event.target.closest('[data-cart-remove]');
      if (!qtyButton && !removeLink) return;

      event.preventDefault();

      const line = Number((qtyButton || removeLink).dataset.cartQty || (qtyButton || removeLink).dataset.cartRemove);
      const stepper = selectors.cartDrawer.querySelector(`[data-cart-line-stepper="${line}"]`);
      const display = stepper?.querySelector('.qty-display');
      const currentQty = Number(display?.textContent || 0);
      const delta = qtyButton ? Number(qtyButton.dataset.qtyDelta || 0) : -currentQty;
      const nextQuantity = Math.max(0, currentQty + delta);
      const article = selectors.cartDrawer.querySelector(`[data-cart-line="${line}"]`);

      if (removeLink && article) {
        article.classList.add('is-removing');
      }

      try {
        const cart = await changeCartLine(line, nextQuantity);
        renderCart(cart);
      } catch (error) {
        console.error(error);
      }
    });
  }

  bindHeaderScroll();
  bindThemeToggle();
  bindPanelControls();
  bindPageTransitions();
  bindButtonRipples();
  bindAccordion();
  bindQuantitySteppers();
  bindProductForms();
  bindProductGallery();
  bindCartDrawerActions();
  bindPredictiveSearch();
  syncCart();
})();
